import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import HandHeart from "./HandHeart";

const childhoodTogether =
  "https://customer-assets-jai6qajn.emergentagent.net/job_9e44ceb2-a107-446e-9b9e-3beb4b654377/artifacts/9ilnu2su_ChatGPT%20Image%2027%20jul%202026%2C%2014_02_07.png";

const engagementVideo =
  "https://customer-assets.emergentagent.com/job_love-story-site-26/artifacts/chktxl0j_vifit.mp4";

const RED = "#891613";

// --- Hand-drawn SVG marks -----------------------------------------------
// Curved arrow pointing DOWN-RIGHT. Use flip=true to point DOWN-LEFT.
const Arrow = ({ className = "", flip = false }) => (
  <svg
    viewBox="0 0 100 100"
    className={className}
    fill="none"
    stroke={RED}
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transform: flip ? "scaleX(-1)" : "none" }}
    aria-hidden="true"
  >
    {/* Wobbly shaft */}
    <path d="M 6 8 C 22 6, 34 18, 42 34 S 60 66, 82 88" />
    {/* Arrowhead — clear V at the tip pointing down-right */}
    <path d="M 82 88 L 66 84" />
    <path d="M 82 88 L 78 70" />
  </svg>
);

const Heart = ({ className = "" }) => (
  <svg
    viewBox="0 0 40 36"
    className={className}
    fill="none"
    stroke={RED}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 32 C 6 22, 2 14, 6 8 C 10 2, 18 4, 20 12 C 22 4, 30 2, 34 8 C 38 14, 34 22, 20 32 Z" />
  </svg>
);
// Heart is currently unused (decorative hearts removed by user request).
// Kept exported-locally so linter doesn't complain and we can reintroduce
// custom hearts (PNG) later without re-adding boilerplate.
void Heart;

// -----------------------------------------------------------------------
const OurStory = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // Subtle parallax on the whole scrapbook block
  const parallaxY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      id="story"
      ref={ref}
      data-testid="story-section"
      className="border-t border-[#c9c3b3] py-20 md:py-24"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex items-start justify-between">
          <p className="corner-label text-[#8a8580]"><span style={{ color: "#891613" }}>(01)</span> — Nuestra historia</p>
        </div>

        {/* Editorial title — tight below the corner label, spans full width */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="display-xl mt-6 md:mt-8"
        >
          Our <span className="italic-serif">story.</span>
        </motion.h2>

        {/* Editorial spread: illustration + text share the same baseline, tight gap */}
        <div className="mt-14 grid grid-cols-12 items-start gap-6 md:mt-20 md:items-center md:gap-7 lg:gap-8">
          {/* Left: scrapbook photo with labels */}
          <div className="col-span-12 md:col-span-6 lg:col-span-7">
            <motion.div
              style={{ y: parallaxY }}
              className="relative mx-auto w-full max-w-[720px]"
              data-testid="story-scrapbook"
            >
              {/* Macarena — handwritten annotation floating above the illustration, out of the faces */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="pointer-events-none absolute top-[3%] left-[6%] z-20 flex flex-col items-start md:top-[4%] md:left-[10%]"
              >
                <span
                  className="hero-script-md leading-none"
                  style={{
                    transform: "rotate(-6deg)",
                    fontSize: "clamp(2.2rem, 4vw, 4rem)",
                  }}
                >
                  Macarena
                </span>
                <Arrow className="-mt-1 h-14 w-16 md:h-16 md:w-20" />
              </motion.div>

              {/* Virginia — handwritten annotation floating above the illustration, out of the faces */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.55 }}
                className="pointer-events-none absolute top-0 right-[6%] z-20 flex flex-col items-end md:-top-2 md:right-[10%]"
              >
                <span
                  className="hero-script-md leading-none"
                  style={{
                    transform: "rotate(6deg)",
                    fontSize: "clamp(2.2rem, 4vw, 4rem)",
                  }}
                >
                  Virginia
                </span>
                <Arrow flip className="-mt-1 h-14 w-16 md:h-16 md:w-20" />
              </motion.div>

              {/* Photo (cut-out PNG with alpha). No frame — clean silhouette. */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="relative mx-auto w-full max-w-[760px]"
              >
                <img
                  src={childhoodTogether}
                  alt="Macarena y Virginia de pequeñas"
                  className="relative block w-full select-none"
                  loading="lazy"
                  draggable={false}
                  data-testid="story-photo-together"
                />
                {/* Hand-drawn heart next to Macarena (girl in blue, on the left) */}
                <HandHeart
                  variant={5}
                  size={54}
                  rotate={-10}
                  className="absolute left-0 top-[38%] z-10 hidden md:block"
                />
              </motion.div>
            </motion.div>
          </div>

          {/* Right: narrative text — vertically centered with the illustration on desktop */}
          <div className="col-span-12 md:col-span-6 lg:col-span-5 relative">
            <HandHeart
              variant={4}
              size={38}
              rotate={12}
              className="absolute -right-2 -top-6 hidden md:block"
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="body-lead text-lg md:text-2xl"
            >
              Estas dos niñas aún no lo sabían…
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.05 }}
              className="mt-8 body-lead"
            >
              Vivimos muchas historias. Crecimos lejos la una de la otra y
              recorrimos caminos muy distintos.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="mt-6 body-lead"
            >
              Un día nos conocimos.
              <br />
              Años después nos reencontramos.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-6 body-lead"
            >
              Y ese reencuentro supo tanto a hogar: las dos estábamos
              exactamente donde necesitábamos estar.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-6 body-lead"
            >
              El resto…{" "}
              <span className="italic-serif">ya es nuestra historia favorita.</span>
            </motion.p>
          </div>
        </div>

        {/* Engagement video — sits immediately below the story with a controlled gap */}
        <motion.figure
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1 }}
          className="mt-16 md:mt-20"
          data-testid="engagement-video"
        >
          <div className="mb-3 flex items-center justify-between">
            <p className="corner-label text-[#8a8580]">Y entonces…</p>
            <p className="corner-label text-[#8a8580]">La pedida</p>
          </div>
          <div className="mx-auto aspect-[16/10] w-full max-w-4xl overflow-hidden md:aspect-[16/9]">
            <video
              src={engagementVideo}
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-cover"
            />
          </div>
          <figcaption className="mt-3 text-center">
            <span className="italic-serif text-lg" style={{ color: RED }}>
              She said yes.
            </span>
          </figcaption>
        </motion.figure>
      </div>
    </section>
  );
};

export default OurStory;
